import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import {
  DocumentDirectoryType,
  IDocumentDirectoryTypeInfo,
  STANDARD_DIRECTORY_INFO,
  SYSTEM_DIRECTORY_INFO_ARRAY,
  getDirectoryType,
} from './documentDirectoryTypes';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Elternordner der Neuanlage; `null` legt einen Ordner auf oberster Ebene an. */
  parent_idDocumentDirectory: number | null;
  parentTitle: string;
  documentDirectoryArray: IDocumentDirectory[];
  onCreate: (
    title: string,
    directoryType: DocumentDirectoryType,
    parent_idDocumentDirectory: number | null
  ) => void;
}

/**
 * Zwischendialog beim Anlegen eines Ordners: zuerst wird die Ordnerart gewaehlt
 * (Standardordner oder Systemordner), danach der Name vergeben.
 */
export const DocumentDirectoryCreateDialog: React.FC<IProps> = props => {
  const [selectedInfo, setSelectedInfo] =
    useState<IDocumentDirectoryTypeInfo | null>(null);
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (props.isOpen) {
      setSelectedInfo(null);
      setTitle('');
    }
  }, [props.isOpen]);

  const existingSystemTypes = props.documentDirectoryArray
    .filter(
      documentDirectory =>
        documentDirectory.Parent_idDocumentDirectory ===
        props.parent_idDocumentDirectory
    )
    .map(documentDirectory => getDirectoryType(documentDirectory));

  const handleSelect = (info: IDocumentDirectoryTypeInfo) => {
    setSelectedInfo(info);
    setTitle(info.defaultTitle);
  };

  const handleCreate = () => {
    if (selectedInfo === null || title.trim() === '') {
      return;
    }

    props.onCreate(
      title.trim(),
      selectedInfo.type,
      props.parent_idDocumentDirectory
    );
    props.setIsOpen(false);
  };

  const renderOption = (
    info: IDocumentDirectoryTypeInfo,
    isDisabled: boolean
  ) => {
    const Icon = info.icon;

    return (
      <ListItemButton
        key={`directory-type-${info.type}`}
        onClick={() => handleSelect(info)}
        disabled={isDisabled}
        sx={{ borderRadius: 1, alignItems: 'flex-start' }}
      >
        <ListItemIcon sx={{ mt: 0.5 }}>
          <Icon color={info.type === 'standard' ? 'action' : 'primary'} />
        </ListItemIcon>
        <ListItemText
          primary={
            isDisabled ? `${info.label} (bereits vorhanden)` : info.label
          }
          secondary={info.description}
        />
      </ListItemButton>
    );
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>
        Neuer Ordner
        <Typography variant='body2' color='text.secondary'>
          {props.parent_idDocumentDirectory === null
            ? 'Wird auf oberster Ebene angelegt'
            : `Wird angelegt in: ${props.parentTitle}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {selectedInfo === null ? (
          <>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Welche Art von Ordner soll erstellt werden?
            </Typography>
            {renderOption(STANDARD_DIRECTORY_INFO, false)}
            <Divider sx={{ my: 2 }}>Systemordner</Divider>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              Systemordner besitzen eine feste fachliche Funktion und spiegeln
              Dokumente aus anderen Bereichen des Vorgangs. Ihr Inhalt wird
              automatisch gepflegt.
            </Typography>
            {SYSTEM_DIRECTORY_INFO_ARRAY.map(info =>
              renderOption(info, existingSystemTypes.includes(info.type))
            )}
          </>
        ) : (
          <Stack spacing={2}>
            <Alert
              severity={selectedInfo.type === 'standard' ? 'info' : 'warning'}
              icon={<selectedInfo.icon fontSize='inherit' />}
            >
              <Typography variant='subtitle2'>{selectedInfo.label}</Typography>
              {selectedInfo.description}
              {selectedInfo.isPreview === true && (
                <Box sx={{ mt: 1 }}>
                  Die eigene fachliche Logik dieses Ordners wird gesondert
                  ausgearbeitet.
                </Box>
              )}
            </Alert>
            <TextField
              label='Ordnername'
              value={title}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(event.target.value)
              }
              size='small'
              autoFocus
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {selectedInfo !== null && (
          <Button variant='text' onClick={() => setSelectedInfo(null)}>
            Zurück
          </Button>
        )}
        <Button variant='outlined' onClick={() => props.setIsOpen(false)}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          disabled={selectedInfo === null || title.trim() === ''}
          onClick={handleCreate}
        >
          Anlegen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
